import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@cokitchen/cokitchen-components';
import { AddIcon } from 'cokitchen-icons';
import { ReactComponent as BackArrow } from 'assets/icons/back_arrow.svg';
import PackagingScreenCard from 'components/kitchen/packaging-screen-card';
import { useNavigate } from 'react-router-dom';
import { apiService } from 'utils/api';
import Loader from 'components/base/loader';
import { PackagingScreenType } from 'models/packaging-screen.model';
import useDashboardContext from 'contexts/dashboard.context';
import InfiniteScroll from 'react-infinite-scroll-component';
import PackagingScreenSearchFilter from 'components/kitchen/packaging-screen-search-filter';
import CreatePackagingScreenModal, {
  FormDataType
} from 'components/kitchen/create-packaging-screen-modal';

const PackagingScreens = () => {
  const navigate = useNavigate();
  const mounting = useRef(true);
  const { activeCokitchen } = useDashboardContext();
  const [loading, setLoading] = useState(false);
  const [screens, setScreens] = useState<PackagingScreenType[]>([]);
  const [totalScreens, setTotalScreens] = useState(0);
  const [query, setQuery] = useState({ active: '', search: '', page: 1, size: 10 });
  const [showCreatePackagingScreenModal, setShowCreatePackagingScreenModal] = useState(false);
  const [showEditPackagingScreen, setShowEditPackagingScreen] = useState(false);
  const [selectedScreen, setSelectedScreen] = useState<PackagingScreenType | null>(null);

  const loadScreens = async (refetch?: boolean) => {
    if (!activeCokitchen.id) return;
    try {
      if (refetch) setLoading(true);
      const params: Record<string, unknown> = {
        page: query.page,
        size: query.size,
        cokitchen_id: activeCokitchen.id
      };
      if (query.search) params.search = query.search;
      if (query.active) params.active = query.active;
      const res = await apiService.get('kitchen/packaging-screens', {
        params,
        baseUrl: process.env.REACT_APP_KITCHEN_API_URL
      });
      const results = res.data.data.results;
      if (query.page > 1) setScreens(screens.concat(results));
      else setScreens(results);
      setTotalScreens(res.data.data.total);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (mounting.current || query.page === 1) loadScreens(true);
    return () => {
      mounting.current = false;
    };
  }, [query.search, query.active]);

  useEffect(() => {
    if (query.page > 1) loadScreens();
  }, [query.page]);

  useEffect(() => {
    const hasScroll = document.body.scrollHeight - window.innerHeight >= 80;
    if (!loading && screens.length < totalScreens && !hasScroll)
      setQuery((q) => ({ ...q, page: ++q.page }));
  }, [screens, totalScreens]);

  const handleEditSuccess = (value: FormDataType) => {
    const screenIndex = screens.findIndex((item) => item.id === value.id);
    if (screenIndex !== -1) {
      const editedScreen = {
        ...screens[screenIndex],
        ...value
      };
      const newScreens = [...screens];
      newScreens.splice(screenIndex, 1, editedScreen);
      setScreens(newScreens);
    }
  };

  return (
    <div className='flex flex-col gap-6'>
      <div className='flex flex-col gap-4'>
        <button className='flex items-center gap-3' onClick={() => navigate(-1)}>
          <span className='w-[30px] h-[30px] flex items-center justify-center bg-[#F4F1F1] rounded-full'>
            <BackArrow />
          </span>
          <p className='text-sm font-medium'>Return to previous</p>
        </button>
        <h3 className='text-2xl font-semibold'>Packaging screen settings</h3>
      </div>
      <div className='w-full flex items-center justify-between flex-wrap gap-3'>
        <PackagingScreenSearchFilter
          query={{
            search: query.search,
            filter: query.active,
            onSearchChange: (search) => setQuery((q) => ({ ...q, search, page: 1 })),
            onFilterChange: (active) => setQuery((q) => ({ ...q, active, page: 1 }))
          }}
        />
        <Button
          variant='link'
          color='primary'
          className='flex items-center gap-[3px]'
          onClick={() => {
            setSelectedScreen(null);
            setShowCreatePackagingScreenModal(true);
          }}>
          <AddIcon color='#E60A2B' />
          Create new screen
        </Button>
      </div>
      {loading ? (
        <Loader />
      ) : screens?.length ? (
        <InfiniteScroll
          hasMore={totalScreens > screens?.length}
          dataLength={screens?.length}
          loader={<p className='text-center mt-8'>Loading...</p>}
          next={() => setQuery((q) => ({ ...q, page: ++q.page }))}>
          <div className='grid gap-6 grid-cols-[repeat(auto-fill,minmax(224px,1fr))]'>
            {screens.map((screen) => (
              <PackagingScreenCard
                key={screen.id}
                screen={screen}
                onEdit={() => {
                  setSelectedScreen(screen);
                  setShowEditPackagingScreen(true);
                }}
              />
            ))}
          </div>
        </InfiniteScroll>
      ) : (
        <div className='h-40 flex items-center justify-center'>
          <p className='flex justify-center'>No screens found</p>
        </div>
      )}
      {showCreatePackagingScreenModal && (
        <CreatePackagingScreenModal
          isOpen={showCreatePackagingScreenModal}
          onClose={() => setShowCreatePackagingScreenModal(false)}
          type='create'
          refreshData={loadScreens}
          screenData={selectedScreen}
        />
      )}
      {showEditPackagingScreen && (
        <CreatePackagingScreenModal
          isOpen={showEditPackagingScreen}
          onClose={() => setShowEditPackagingScreen(false)}
          type='edit'
          screenData={selectedScreen}
          onEditSuccess={handleEditSuccess}
        />
      )}
    </div>
  );
};

export default PackagingScreens;
